/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { FC, useEffect, useMemo, useState } from "react";
import Grid from "components/generals/Grid";
import Box from "components/generals/Box";
import { H1 } from "components/generals/_Titles";
import { FormProps } from "interfaces";
import { HeaderEnum, PersonInterface, UserInterface } from "medicalsystem-shared";
import PersonForm from "../PersonForm";
import UserForm from "../UserForm";
import FormActionButtons from "../custom/FormActionButtons";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { FormActionType } from "types";

/**
 * Comprehensive form for creating/editing system user accounts.
 * Combines two sub-forms: PersonForm (demographics) and UserForm (credentials and roles).
 * Manages state synchronization between the person and user data.
 * 
 * Features:
 * - PersonForm: Personal information, address, contacts, emergency contacts
 * - UserForm: Username, email, password, roles, active status
 * - Cascading updates: Changes in sub-forms update the main model
 * - Automatic person data fetching based on user ID
 * - Form validation across both sections
 * - List update handling for table refresh
 * - Modal and page modes
 * - Password modification support
 * 
 * State Management:
 * - Fetches person data associated with user on mount
 * - Handles nested updates (user.person)
 * - Synchronizes person and user state
 * - Updates user list on successful save
 * 
 * Use Cases:
 * - Creating new system user accounts (admin, receptionist, etc.)
 * - Editing existing user information
 * - Administrative user management
 * - User account provisioning workflows
 * 
 * @param {FormProps<UserInterface>} props - Component props
 * @returns {React.ReactElement} The complete user account form
 */
const UserFullForm: FC<FormProps<UserInterface>> = ({
  model,
  action,
  isModal,
  onCloseAction,
  modelList,
  setModelList,
}) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [person, setPerson] = useState<Partial<PersonInterface>>();

  useEffect(() => {
    sendRequest(CoreAPI_URLs.PERSONS_GET_BY_USER_ID, onGetPersonHandler, "GET", null, [
      { header: HeaderEnum.RESOURCE_ID, value: user.id },
    ]);
  }, []);
  const onGetPersonHandler = (personResponse: Partial<PersonInterface>) => {
    setPerson(personResponse);
  };
  const user = useMemo(() => model || {}, [model]);

  const handlePersonUpdate = (updatedPerson: Partial<PersonInterface>) => {
    const updatedModel = { ...model, person: { ...person, ...updatedPerson } };
    updateModelList(updatedModel);
  };

  const handleUserUpdate = (updatedUser: Partial<UserInterface>) => {
    const updatedModel = {
      ...model,
      ...updatedUser,
      person: { ...person, user: { ...user, ...updatedUser } },
    };
    updateModelList(updatedModel);
  };

  const updateModelList = (updatedModel: any) => {
    const updatedList = modelList?.map((item) =>
      item.id === updatedModel.id ? updatedModel : item
    );
    setModelList?.(updatedList);
  };

  return (
    <Box className="full-form-wrapper" py={0}>
      <H1 className={isModal ? "title-modal" : ""}>New User</H1>
      <Grid container justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Box className="full-form-sub-wrapper">
            {(person || action === FormActionType.NEW) && (
              <PersonForm action={action} model={person} updateListHandler={handlePersonUpdate} />
            )}
          </Box>
          <Box className="full-form-sub-wrapper">
            <UserForm action={action} model={user} updateListHandler={handleUserUpdate} person={person} modifyPassword={true}/>
          </Box>
        </Grid>
      </Grid>
      <Grid className="btn-close-modal">
        {" "}
        <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
      </Grid>
    </Box>
  );
};

export default UserFullForm;
